// 70x22 SVG polyline sparkline for the Matrix's Trend column.
//
// Normalised to the row's local min/max so a flat row still produces a
// visible mid-line. Stroke colour is supplied by the parent (derived from
// the row's last-cell tone, or "#fff" for the org row so it shows against
// navy). Pure presentation — no state, no effects.
//
// Extracted from Matrix.tsx to keep the per-component cognitive complexity
// below SonarCloud S3776 (15).

interface MatrixSparklineProps {
  /** All values must be non-null; the parent omits the sparkline otherwise. */
  values: number[];
  stroke: string;
}

const VIEW_WIDTH = 70;
const VIEW_HEIGHT = 22;
const PADDING_X = 2;
const PADDING_Y_BOTTOM = 20;
const DRAW_WIDTH = 66; // VIEW_WIDTH - 2 * PADDING_X
const DRAW_HEIGHT = 18; // PADDING_Y_BOTTOM - PADDING_TOP(2)

function buildPoints(values: number[]): string {
  const max = Math.max(...values);
  const min = Math.min(...values);
  const range = max - min || 1;
  const denom = values.length > 1 ? values.length - 1 : 1;
  return values
    .map((v, i) => {
      const x = PADDING_X + (i * DRAW_WIDTH) / denom;
      const y = PADDING_Y_BOTTOM - ((v - min) / range) * DRAW_HEIGHT;
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(' ');
}

export function MatrixSparkline({ values, stroke }: Readonly<MatrixSparklineProps>) {
  if (values.length === 0) return <span aria-hidden>—</span>;
  const points = buildPoints(values);
  return (
    <svg
      className="block mx-auto h-[22px] w-[70px]"
      viewBox={`0 0 ${VIEW_WIDTH} ${VIEW_HEIGHT}`}
      role="img"
      aria-label="Trend sparkline"
      data-testid="matrix-sparkline"
    >
      <polyline fill="none" stroke={stroke} strokeWidth={1.8} points={points} />
    </svg>
  );
}
